import { useEffect, useState } from "react";
import { SvgIcon } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import StoreIcon from "@mui/icons-material/Store";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import EngineeringIcon from "@mui/icons-material/Engineering";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HandshakeIcon from "@mui/icons-material/Handshake";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SettingsIcon from "@mui/icons-material/Settings";
import { AccountBalanceWallet, Checklist, LocalAtm } from "@mui/icons-material";
import InventoryIcon from "@mui/icons-material/Inventory";
import PaymentsIcon from "@mui/icons-material/Payments";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ContactsIcon from "@mui/icons-material/Contacts";
import AssessmentIcon from "@mui/icons-material/Assessment";
import InsightsIcon from "@mui/icons-material/Insights";
import PercentIcon from "@mui/icons-material/Percent";
import CampaignIcon from "@mui/icons-material/Campaign";
import SummarizeIcon from "@mui/icons-material/Summarize";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ConstructionIcon from "@mui/icons-material/Construction";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useAuthContext } from "src/contexts/auth-context";
import { getProyectosFromUser } from "src/services/proyectosService";
import { modoLecturaEnProyecto } from "src/utils/permisos/accionesPorProyecto";
import { getEmpresaDetailsFromUser } from "src/services/empresaService";
import { vistasVisiblesPara } from "src/config/vistasUtiles";
import { esAdminDeCajas } from "src/config/pestanasObra";
import { ITEM_SORBY_IA, itemsMenuAltaSola, sorbyIaPrimero, tieneMenuRecortado } from "src/utils/sorbyIa/cuentaSorbyIa";
import { consultarEstadoSorbyIa } from "src/hooks/useOnboardingWeb";
import { itemsMenuModulos, marcarModulosEnMenu } from "src/utils/sorbyIa/modulosPrueba";

const icon = (Icon) => (
  <SvgIcon fontSize="small">
    <Icon />
  </SvgIcon>
);

export const getPermisosVisibles = (empresaAcciones, permisosOcultos = []) =>
  (empresaAcciones || []).filter((accion) => !(permisosOcultos || []).includes(accion));

// Navegación dedicada de corralón: reorganizada por "lo que hace el usuario"
// (Ventas / Clientes / Materiales / Caja y reportes / Configuración) en vez de
// por categoría abstracta. Ver docs/corralones/11-propuesta-funcionalidades.md.
function buildCorralonGroups({ user, empresa, permisosUsuario, esAdmin }) {
  const empId = empresa.id;
  const groups = [];

  // ——— INICIO ———
  const inicioItems = [];
  inicioItems.push({ title: "Asistente IA (Beta)", path: "/agente", icon: icon(AutoAwesomeRoundedIcon) });
  if (user?.admin) {
    inicioItems.push({ title: "Panel de Control", path: "/control-panel", icon: icon(AdminPanelSettingsIcon) });
  }
  inicioItems.push({ title: "Dashboard corralón", path: `/dashboard-corralon?empresaId=${empId}`, icon: icon(DashboardIcon) });
  groups.push({ id: "inicio", label: "Inicio", alwaysOpen: true, items: inicioItems });

  // ——— VENTAS (el corazón operativo) ———
  const ventasItems = [];
  ventasItems.push({ title: "Ventas", path: "/ventas", icon: icon(PaymentsIcon) });
  ventasItems.push({ title: "Acopio", path: `/acopios?empresaId=${empId}`, icon: icon(InventoryIcon) });
  ventasItems.push({ title: "Devoluciones", path: "/devoluciones", icon: icon(AssignmentReturnIcon) });
  ventasItems.push({ title: "Cobranzas", path: "/cobros-cliente", icon: icon(AttachMoneyIcon) });
  groups.push({ id: "ventas", label: "Ventas", items: ventasItems });

  // ——— CLIENTES (ambas entradas por ahora, a pedido) ———
  const clientesItems = [];
  clientesItems.push({ title: "Clientes", path: "/clientes", icon: icon(PeopleIcon) });
  clientesItems.push({ title: "Grupos de cliente", path: "/grupos-cliente", icon: icon(PeopleIcon) });
  groups.push({ id: "clientes", label: "Clientes", items: clientesItems });

  // ——— MATERIALES ———
  const materialesItems = [];
  materialesItems.push({ title: "Stock de materiales", path: `/stockMateriales?empresaId=${empId}`, icon: icon(InventoryIcon) });
  materialesItems.push({ title: "Qué entregar", path: "/que-entregar", icon: icon(EventAvailableIcon) });
  materialesItems.push({ title: "Recepción de proveedor", path: "/recepcion-proveedor", icon: icon(MoveToInboxIcon) });
  materialesItems.push({ title: "Proveedores", path: "/proveedores", icon: icon(StoreIcon) });
  groups.push({ id: "materiales", label: "Materiales", items: materialesItems });

  // ——— CAJA Y REPORTES ———
  const cajaItems = [];
  if (esAdmin) {
    cajaItems.push({ title: "Todos los movimientos", path: `/cajas?empresaId=${empId}&vista=todos`, icon: icon(AccountBalanceWallet) });
    if (permisosUsuario.includes("VER_REVISION_FACTURAS")) {
      cajaItems.push({ title: "Revisión de facturas", path: `/revisionFacturas?empresaId=${empId}`, icon: icon(Checklist) });
    }
  }
  if (permisosUsuario.includes("EMITIR_COMPROBANTE_FISCAL")) {
    // Primera pantalla propia del módulo de facturación ARCA (#401): antes sólo vivía
    // embebida dentro de un movimiento de caja. `EMITIR_COMPROBANTE_FISCAL` es general
    // (no de corralón), pero corralón tiene su propio armado de navegación.
    cajaItems.push({ title: "Facturación ARCA", path: `/facturacionArca?empresaId=${empId}`, icon: icon(ReceiptLongIcon) });
  }
  cajaItems.push({ title: "Reportes", path: "/reportes", icon: icon(AssessmentIcon) });
  groups.push({ id: "caja", label: "Caja y reportes", items: cajaItems });

  // ——— CONFIGURACIÓN ———
  const configItems = [];
  configItems.push({ title: "Sucursales", path: "/sucursales", icon: icon(StoreIcon) });
  configItems.push({ title: "Mi cuenta", path: "/account", icon: icon(PeopleIcon) });
  if (user?.admin) {
    configItems.push({ title: "Configurar " + empresa.nombre, path: `/empresa?empresaId=${empId}`, icon: icon(SettingsIcon) });
  }
  if (permisosUsuario.includes("ADMIN_USUARIOS")) {
    configItems.push({ title: "Administración", path: `/configuracionBasica/?empresaId=${empId}`, icon: icon(SettingsIcon) });
  }
  groups.push({ id: "configuracion", label: "Configuración", items: configItems });

  return groups;
}

async function buildDefaultGroups({ user, empresa, permisosUsuario, onboardingActivo, modulos }) {
  const empId = empresa.id;
  const esAdmin = esAdminDeCajas(permisosUsuario);
  const esCorralon = empresa?.vertical === "corralon";

  // Corralón usa una navegación propia, reorganizada por flujo de trabajo.
  if (esCorralon) {
    return buildCorralonGroups({ user, empresa, permisosUsuario, esAdmin });
  }

  // Cuenta de alta sola: menú recortado a Sorby IA, Movimientos, Presupuestos y Reportes.
  if (tieneMenuRecortado(empresa)) {
    const iconos = {
      "sorby-ia": AutoAwesomeRoundedIcon,
      movimientos: AccountBalanceWallet,
      presupuestos: NoteAltIcon,
      reportes: AssessmentIcon,
      "control-obra": EngineeringIcon,
      acopio: InventoryIcon,
      "stock-materiales": InventoryIcon,
    };
    const items = [
      ...itemsMenuAltaSola({ empresaId: empId, esAdmin }),
      ...itemsMenuModulos(modulos, { empresaId: empId, permisos: permisosUsuario }),
    ].map(({ id, title, path }) => ({
      title,
      path,
      icon: icon(iconos[id]),
    }));
    return [{ id: "inicio", label: "Inicio", alwaysOpen: true, items }];
  }

  const groups = [];

  // ——— INICIO ———
  const inicioItems = [];
  // Cuenta en onboarding que ya no es de alta sola: menú completo, Sorby IA primero.
  if (sorbyIaPrimero(empresa, onboardingActivo)) {
    inicioItems.push({ title: ITEM_SORBY_IA.title, path: ITEM_SORBY_IA.path, icon: icon(AutoAwesomeRoundedIcon) });
  }
  inicioItems.push({ title: "Asistente IA (Beta)", path: "/agente", icon: icon(AutoAwesomeRoundedIcon) });
  if (user?.admin) {
    inicioItems.push({ title: "Panel de Control", path: "/control-panel", icon: icon(AdminPanelSettingsIcon) });
  }
  if (esAdmin) {
    inicioItems.push({ title: "Resumen general", path: `/vistaResumen?empresaId=${empId}`, icon: icon(DashboardIcon) });
  }
  inicioItems.push({ title: "Reportes", path: "/reportes", icon: icon(AssessmentIcon) });
  groups.push({ id: "inicio", label: "Inicio", alwaysOpen: true, items: inicioItems });

  // ——— VISTAS ÚTILES ———
  // Dashboards que cruzan varios datasets en un pantallazo (no son cajas ni reportes sueltos).
  // Data-driven desde el catálogo VISTAS_UTILES, filtrado por la config de empresa y de usuario.
  const vistasItems = [];
  vistasVisiblesPara(empresa, user, permisosUsuario)
    // Las vistas con permiso propio las ve quien lo tenga; el resto siguen siendo de admin.
    .filter((v) => (v.permiso ? true : esAdmin))
    .forEach((v) => {
      vistasItems.push({ title: v.title, path: v.path, icon: icon(InsightsIcon) });
    });
  if (esAdmin) {
    // El configurador siempre presente, para poder reactivar vistas apagadas.
    vistasItems.push({ title: "Configurar vistas", path: "/vistas/configurador", icon: icon(SettingsIcon) });
  }
  if (vistasItems.length > 0) groups.push({ id: "vistas", label: "Vistas útiles", items: vistasItems });

  // ——— FINANZAS ———
  const finanzasItems = [];
  if (esAdmin) {
    finanzasItems.push({ title: "Todos los movimientos", path: `/cajas?empresaId=${empId}&vista=todos`, icon: icon(DashboardIcon) });
  }
  if (permisosUsuario.includes("VER_MI_CAJA_CHICA")) {
    finanzasItems.push({ title: "Caja chica", path: "/cajaChica", icon: icon(AttachMoneyIcon) });
  }
  if (esAdmin && permisosUsuario.includes("VER_MI_CAJA_CHICA")) {
    finanzasItems.push({ title: "Todas las cajas chicas", path: "/perfilesEmpresa", icon: icon(AttachMoneyIcon) });
  }
  if (!esCorralon) {
    // "Mis reservas": vista del participante (total + desglose por obra). No exige
    // VER_RESERVAS_OBRA: un operador puede participar de reservas sin permiso global;
    // la página muestra vacío si no participa en ninguna.
    finanzasItems.push({ title: "Mis reservas", path: "/misReservas", icon: icon(AccountBalanceWallet) });
  }
  if (permisosUsuario.includes("VER_RESERVAS_OBRA") && !esCorralon) {
    // Reserva de Obra: reserva interna de fondos por obra (≠ caja chica personal).
    // Visible solo con la acción VER_RESERVAS_OBRA configurada en la empresa.
    finanzasItems.push({ title: "Reservas por obra", path: "/reservasObra", icon: icon(AccountBalanceWallet) });
  }
  if (permisosUsuario.includes("VER_CONTROL_PAGOS")) {
    finanzasItems.push({ title: "Control de pagos", path: "/control-pagos", icon: icon(LocalAtm) });
  }
  if (permisosUsuario.includes("VER_GASTOS_RECURRENTES")) {
    finanzasItems.push({ title: "Gastos recurrentes", path: "/gastos-recurrentes", icon: icon(LocalAtm) });
  }
  if (permisosUsuario.includes("GESTIONAR_PROVEEDORES") || permisosUsuario.includes("VER_CUENTA_CORRIENTE_PROVEEDORES")) {
    finanzasItems.push({ title: "Proveedores", path: "/proveedores", icon: icon(StoreIcon) });
  }
  if (permisosUsuario.includes("GESTIONAR_INVERSORES") && !esCorralon) {
    finanzasItems.push({ title: "Inversores", path: "/inversores", icon: icon(HandshakeIcon) });
  }
  if (permisosUsuario.includes("VER_PLANES_COBRO") && !esCorralon) {
    // Plan de cobros (PlanCobroModel) es para constructoras. En corralón no aplica.
    finanzasItems.push({ title: "Plan de cobros", path: "/cobros", icon: icon(AttachMoneyIcon) });
  }
  if (permisosUsuario.includes("VER_PLANES_PAGO") && !esCorralon) {
    // Planes de pago a proveedores (PlanPago, lado costo). Espejo de "Plan de cobros".
    finanzasItems.push({ title: "Planes de pago", path: "/pagos-proveedores", icon: icon(PaymentsIcon) });
  }
  if (esAdmin && !esCorralon) {
    // Control de presupuestos / presupuestos profesionales son de obra (constructora).
    finanzasItems.push({ title: "Control presupuestos", path: "/control-presupuestos", icon: icon(NoteAltIcon) });
    if (permisosUsuario.includes("VER_PRESUPUESTOS_PROFESIONALES")) {
      finanzasItems.push({ title: "Presupuestos profesionales", path: "/presupuestosProfesionales", icon: icon(NoteAltIcon) });
    }
  }
  // Control de Obra: acceso explícito por acción (no por la heurística de admin).
  if (permisosUsuario.includes("VER_CONTROL_OBRA") && !esCorralon) {
    finanzasItems.push({ title: "Control de Obra", path: "/control-obra", icon: icon(EngineeringIcon) });
  }
  if (permisosUsuario.includes("EMITIR_COMPROBANTE_FISCAL")) {
    // Primera pantalla propia del módulo de facturación ARCA (#401): antes sólo vivía
    // embebida dentro de un movimiento de caja.
    finanzasItems.push({ title: "Facturación ARCA", path: `/facturacionArca?empresaId=${empId}`, icon: icon(ReceiptLongIcon) });
  }
  if (finanzasItems.length > 0) groups.push({ id: "finanzas", label: "Finanzas", items: finanzasItems });

  // ——— CLIENTES ———
  // La entidad Cliente la comparten corralón, Plan de Cobros y Comercialización,
  // pero la pantalla solo estaba en el menú de corralón: una desarrolladora no
  // tenía por dónde entrar al ABM (alta con CUIT, alias, fusión de duplicados).
  // `VER_CLIENTES` era hasta ahora solo un intent del bot; pasa a gobernar
  // también la visibilidad de la pantalla.
  if (permisosUsuario.includes("VER_CLIENTES")) {
    groups.push({
      id: "clientes",
      label: "Clientes",
      items: [{ title: "Clientes", path: "/clientes", icon: icon(PeopleIcon) }],
    });
  }

  // ——— COMERCIALIZACIÓN ———
  // Grupo propio, no una entrada de Configuración: el desarrollador entra acá
  // todos los días. Permiso separado del de obra a propósito — precios, clientes
  // y comisiones es justo lo que un tercero en obra no tiene que ver.
  const comercializacionItems = [];
  if (permisosUsuario.includes("VER_COMERCIALIZACION") && !esCorralon) {
    comercializacionItems.push({ title: "Unidades", path: "/comercializacion", icon: icon(ApartmentIcon) });
    comercializacionItems.push({ title: "Operaciones", path: "/comercializacion/operaciones", icon: icon(HandshakeIcon) });
    comercializacionItems.push({ title: "Comisiones", path: "/comercializacion/comisiones", icon: icon(PercentIcon) });
    comercializacionItems.push({ title: "Tablero", path: "/comercializacion/tablero", icon: icon(InsightsIcon) });
  }
  // Configurar los estados es escritura: no se lo mostramos a quien solo mira.
  if (permisosUsuario.includes("EDITAR_COMERCIALIZACION") && !esCorralon) {
    comercializacionItems.push({ title: "Estados", path: "/comercializacion/estados", icon: icon(SettingsIcon) });
  }
  if (comercializacionItems.length > 0) {
    groups.push({ id: "comercializacion", label: "Comercialización", items: comercializacionItems });
  }

  // ——— MATERIALES ———
  const materialesItems = [];
  if (permisosUsuario.includes("VER_STOCK_MATERIALES")) {
    materialesItems.push({ title: "Stock de materiales", path: `/stockMateriales?empresaId=${empId}`, icon: icon(InventoryIcon) });
  }
  if (permisosUsuario.includes("VER_REMITOS")) {
    materialesItems.push({ title: "Remitos", path: `/remitos?empresaId=${empId}`, icon: icon(ReceiptLongIcon) });
  }
  if (permisosUsuario.includes("VER_INVENTARIO_PRODUCTOS")) {
    materialesItems.push({ title: "Inventario productos", path: `/inventarioProductos?empresaId=${empId}`, icon: icon(InventoryIcon) });
  }
  if (permisosUsuario.includes("CREAR_ACOPIO")) {
    materialesItems.push({ title: "Acopio", path: `/acopios?empresaId=${empId}`, icon: icon(InventoryIcon) });
  }
  if (permisosUsuario.includes("VER_NOTAS_DE_PEDIDO")) {
    materialesItems.push({ title: "Notas de pedido", path: "/notaPedido", icon: icon(NoteAltIcon) });
  }
  if (permisosUsuario.includes("VER_STOCK_SOLICITUDES")) {
    materialesItems.push({ title: "Tickets de stock", path: `/stockSolicitudes?empresaId=${empId}`, icon: icon(NoteAltIcon) });
  }
  if (permisosUsuario.includes("VER_STOCK_MOVIMIENTOS")) {
    materialesItems.push({ title: "Movimientos de stock", path: `/stockMovimientos?empresaId=${empId}`, icon: icon(CompareArrowsIcon) });
  }
  if (
    permisosUsuario.includes("VER_STOCK_MATERIALES") ||
    permisosUsuario.includes("VER_STOCK_SOLICITUDES")
  ) {
    materialesItems.push({ title: "Materiales por obra", path: `/stockVistaObra?empresaId=${empId}`, icon: icon(ConstructionIcon) });
    if (empresa?.stock_config?.caja_a_stock === true) {
      materialesItems.push({ title: "Materiales pendientes", path: "/materialesPendientes", icon: icon(ReceiptLongIcon) });
    }
  }
  if (materialesItems.length > 0) groups.push({ id: "materiales", label: "Materiales", items: materialesItems });

  // ——— OBRAS ———
  if (esAdmin) {
    let proys = await getProyectosFromUser(user);
    // Read-gating por obra (TAR-393): ocultar del sidenav las obras donde la lectura quedó
    // bloqueada por override (VER_CAJAS/LISTAR quitados y sin VER_MIS). B0-safe: sin override → 'todo'.
    const userLect = { ...user, empresa: { acciones: empresa?.acciones || [] } };
    proys = (proys || []).filter(
      (p) => modoLecturaEnProyecto(userLect, p.id) !== "bloqueado"
    );
    // getProyectosFromUser ya descarta las eliminadas, así que acá `activo === false`
    // es genuinamente "obra cerrada" y no un borrado lógico (TAR-763).
    const activas = proys.filter((p) => p.activo);
    const inactivas = proys.filter((p) => !p.activo);

    // Entrar a una obra es entrar a su vista de proyecto, con las pestañas de `/obra` incluidas.
    const itemDeObra = (proy) => ({
      title: proy.nombre,
      path: `/proyecto?proyectoId=${proy.id}`,
      icon: (
        <SvgIcon fontSize="small" sx={{ color: proy.activo ? "success.main" : "text.disabled" }}>
          <StoreIcon />
        </SvgIcon>
      ),
    });

    if (activas.length > 0 || inactivas.length > 0) {
      const finGroup = groups.find((g) => g.id === "finanzas");
      if (finGroup) {
        finGroup.items.push({ title: "Cajas", path: "/cajas", icon: icon(AccountBalanceWallet) });
        finGroup.items.push({ title: "Proyectos", path: "/proyectos", icon: icon(StoreIcon) });
      }
    }
    if (activas.length > 0) {
      groups.push({ id: "obras", label: "Obras", items: activas.map(itemDeObra) });
    }
    if (inactivas.length > 0) {
      groups.push({
        id: "obras-inactivas",
        label: "Obras inactivas",
        items: inactivas.map((proy) => ({ ...itemDeObra(proy), badge: "Inactiva" })),
      });
    }
  }

  // ——— REVISIÓN ———
  const revisionItems = [];
  if (esAdmin && permisosUsuario.includes("VER_REVISION_FACTURAS")) {
    revisionItems.push({ title: "Revisión de facturas", path: `/revisionFacturas?empresaId=${empId}`, icon: icon(DashboardIcon) });
  }
  if (permisosUsuario.includes("VER_VALIDACION_BORRADORES")) {
    revisionItems.push({ title: "Validación borradores", path: `/panelValidacion?empresaId=${empId}`, icon: icon(Checklist) });
  }
  if (revisionItems.length > 0) groups.push({ id: "revision", label: "Revisión", items: revisionItems });

  // ——— GESTIÓN ———
  const gestionItems = [];
  if (user?.sdr === true) {
    gestionItems.push({ title: "Contactos SDR", path: "/contactosSDR", icon: icon(ContactsIcon) });
  }
  if (user?.admin) {
    gestionItems.push({ title: "Candidatos a mensaje", path: "/candidatosMensajes", icon: icon(ContactsIcon) });
    gestionItems.push({ title: "Sorby IA: pasos", path: "/sorby-ia-config", icon: icon(SettingsIcon) });
  }
  gestionItems.push({ title: "Mi cuenta", path: "/account", icon: icon(PeopleIcon) });
  groups.push({ id: "gestion", label: "Gestión", items: gestionItems });

  // ——— CONFIGURACIÓN ———
  const configItems = [];
  if (user?.admin) {
    configItems.push({ title: "Configurar " + empresa.nombre, path: `/empresa?empresaId=${empId}`, icon: icon(SettingsIcon) });
  }
  configItems.push({ title: "Plantillas y logos", path: "/plantillas-pdf", icon: icon(AutoAwesomeRoundedIcon) });
  if (permisosUsuario.includes("ADMIN_USUARIOS")) {
    configItems.push({ title: "Administración", path: `/configuracionBasica/?empresaId=${empId}`, icon: icon(SettingsIcon) });
  }
  if (permisosUsuario.includes("INTEGRACION_ODOO")) {
    configItems.push({ title: "Integración con Odoo", path: `/odooIntegracion?empresaId=${empId}`, icon: icon(NoteAltIcon) });
  }
  if (configItems.length > 0) groups.push({ id: "configuracion", label: "Configuración", items: configItems });

  return marcarModulosEnMenu(groups, modulos, { empresaId: empId, permisos: permisosUsuario });
}

const INITIAL_STATE = {
  loading: true,
  empresa: null,
  navType: null, // 'default' | 'celulandia' | 'dhn' | 'onboarding' | 'suspended' | null
  permisos: [],
  groups: [],
};

// Hook compartido que arma la navegación del dashboard.
// Es la fuente de verdad de qué módulos ve el usuario según permisos:
// lo consume el SideNav y también el AgentLauncherDialog para listar páginas.
export function useDashboardNavGroups() {
  const { user, originalUser } = useAuthContext();
  const [state, setState] = useState(INITIAL_STATE);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const emp = await getEmpresaDetailsFromUser(user);
      if (cancelled) return;

      if (emp?.cuenta_suspendida) {
        setState({ loading: false, empresa: null, navType: "suspended", permisos: [], groups: [] });
        return;
      }

      if (!emp) {
        setState({
          loading: false,
          empresa: null,
          navType: "onboarding",
          permisos: [],
          groups: [
            {
              id: "onboarding",
              label: null,
              alwaysOpen: true,
              items: [{ title: "Onboarding", path: "/onboarding", icon: icon(DashboardIcon) }],
            },
          ],
        });
        return;
      }

      const permisosUsuario = getPermisosVisibles(emp.acciones || [], user?.permisosOcultos || []);

      if (permisosUsuario.some((p) => p.startsWith("CELULANDIA_"))) {
        setState({
          loading: false,
          empresa: emp,
          navType: "celulandia",
          permisos: permisosUsuario,
          groups: [],
        });
        return;
      }

      // DHN_SOLO_LECTURA dispara el sidenav DHN por sí solo (sin MOCK_DHN),
      // así el rol solo-lectura ve las pantallas filtradas sin necesitar acceso completo.
      if (permisosUsuario.includes("MOCK_DHN") || permisosUsuario.includes("DHN_SOLO_LECTURA")) {
        setState({
          loading: false,
          empresa: emp,
          navType: "dhn",
          permisos: permisosUsuario,
          groups: [],
        });
        return;
      }

      const { onboardingActivo, modulos } = await consultarEstadoSorbyIa(emp);
      if (cancelled) return;
      const groups = await buildDefaultGroups({
        user,
        empresa: emp,
        permisosUsuario,
        onboardingActivo,
        modulos,
      });
      if (cancelled) return;

      // Panel interno de Sorby (clientes/cobranzas/reportes). Solo si la empresa
      // de la cuenta real tiene acceso_admin_sorby: espiando no aparece.
      const espiando = !!originalUser && originalUser.id !== user?.id;
      const empCuenta = espiando ? await getEmpresaDetailsFromUser(originalUser) : emp;
      if (cancelled) return;
      if (empCuenta?.acceso_admin_sorby) {
        groups.push({
          id: "sorby-admin",
          label: "Sorby Admin",
          items: [
            { title: "Resumen del mes", path: "/admin-resumen", icon: icon(SummarizeIcon) },
            { title: "Proyección", path: "/admin-proyeccion", icon: icon(TrendingUpIcon) },
            { title: "Reporte mensual", path: "/admin-reporte-mensual", icon: icon(AssessmentIcon) },
            { title: "Reporte de marketing", path: "/reporte-marketing", icon: icon(InsightsIcon) },
            { title: "Campañas", path: "/campanas", icon: icon(CampaignIcon) },
            { title: "Links QR", path: "/admin-link-qr", icon: icon(QrCode2Icon) },
            { title: "Reporte de ventas", path: "/reporte-ventas", icon: icon(HandshakeIcon) },
            { title: "Clientes", path: "/admin-clientes", icon: icon(PeopleIcon) },
            { title: "Ventas", path: "/admin-ventas", icon: icon(PaymentsIcon) },
            { title: "Liquidaciones", path: "/admin-liquidaciones", icon: icon(ReceiptLongIcon) },
            { title: "Cobranzas", path: "/admin-cobranzas", icon: icon(AttachMoneyIcon) },
            { title: "Control de facturación", path: "/admin-control-facturacion", icon: icon(ReceiptLongIcon) },
            { title: "Deuda / Mora", path: "/admin-mora", icon: icon(ReportProblemIcon) },
            { title: "Reportes", path: "/admin-reportes", icon: icon(AssessmentIcon) },
            { title: "Configuración", path: "/admin-config", icon: icon(SettingsIcon) },
          ],
        });
      }

      setState({
        loading: false,
        empresa: emp,
        navType: "default",
        permisos: permisosUsuario,
        groups,
      });
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [user, originalUser?.id]);

  return state;
}
